<script setup lang="ts">
import { ref, computed, watch } from "vue";
import SidebarNavigation from "../../sidebar/sidebarNavigation.vue";

const props = withDefaults(
  defineProps<{
    filter: any;
    activeValue: any;
  }>(),
  {
    filter: () => ({}),
    activeValue: "",
  }
);

const emit = defineEmits<{
  (e: "update", value: string): void;
}>();

const getImageUrl = (value: string) => {
  if (!props.filter.image) return "";
  const processedVal = props.filter.lowercaseImage !== false ? value.toLowerCase() : value;
  return props.filter.image.replace("{value}", processedVal);
};

const updateValue = (val: string) => {
  emit("update", val);
};

const itemKeys = computed(() => Object.keys(props.filter.values || {}));
const itemRefs = ref<HTMLElement[]>([]);
const scrollContainer = ref<HTMLElement | null>(null);
const manualIndex = ref(0);

const currentIndex = computed({
  get: () => manualIndex.value,
  set: (val) => {
    manualIndex.value = val;
    scrollToIndex(val);
  }
});

const scrollToIndex = (index: number) => {
  const el = itemRefs.value[index];
  if (el && scrollContainer.value) {
    const container = scrollContainer.value;
    const elLeft = el.offsetLeft;
    const elWidth = el.clientWidth;
    const containerWidth = container.clientWidth;
    const targetScrollLeft = elLeft - (containerWidth / 2) + (elWidth / 2);
    container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  }
};

watch(() => props.activeValue, (newVal) => {
  const idx = itemKeys.value.indexOf(newVal?.toString() || '');
  if (idx >= 0) manualIndex.value = idx;
}, { immediate: true });

const onScroll = () => {
  if (!scrollContainer.value) return;
  const container = scrollContainer.value;
  const containerCenter = container.scrollLeft + container.clientWidth / 2;

  let closestIndex = 0;
  let minDistance = Infinity;

  itemRefs.value.forEach((el, index) => {
    if (!el) return;
    const elCenter = el.offsetLeft + el.clientWidth / 2;
    const distance = Math.abs(elCenter - containerCenter);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  manualIndex.value = closestIndex;
};
</script>

<template>
  <div class="flex flex-col mt-6">
    <div
      ref="scrollContainer"
      class="overflow-x-auto no-scrollbar scroll-smooth"
      @scroll="onScroll"
    >
      <div class="flex gap-8 pb-2 px-2 h-32">
        <div
          v-for="(label, val, index) in filter.values"
          :key="val"
          :ref="el => { if (el && index !== undefined) itemRefs[index] = el as any }"
          class="flex flex-col justify-center items-center cursor-pointer min-w-fit"
          @click="updateValue(val.toString())"
        >
          <div
            class="w-16 h-16 overflow-hidden rounded-full flex items-center justify-center mb-2 transition-all duration-300"
            :class="activeValue === val ? 'shadow-md w-20 h-20 ring-1 ring-brand-main ring-opacity-50' : ''"
          >
            <img
              :src="getImageUrl(val.toString())"
              :alt="label"
              class="w-full h-full object-contain opacity-80"
              @error="(e: any) => e.target.style.display = 'none'"
            />
          </div>
          <span class="text-sm text-gray-500 tracking-wide whitespace-nowrap"
                :class="activeValue === val ? 'font-bold' : ''"
          >{{ label }}</span>
        </div>
      </div>
    </div>
    <sidebar-navigation
      :total="itemKeys.length"
      v-model="currentIndex"
      :container="scrollContainer"
    />
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>